import { CoObjectTypeEnum } from "../../api/models";
import { Dataset } from "../../api/models/dataset";
import { AnalyticalWorkspace } from "../../api/models/analytical-workspace";
import { DatasetField } from "../../api/models/dataset-field";
import { Process } from "../../api/models/process";
import { DimensionDetail } from "../../api/models/dimension-detail";
import { DimensionParameter } from "../../api/models/dimension-parameter";
import { FormatImage } from "../../api/models/format-image";
import { ComponentReference } from "../../api/models/component-reference";
import { TgkDropDownItem } from "../../api/models/tgk-dropdown-item";
import { FormatMetadata } from "../../api/models/format-metadata";
import { TemplateInfo } from "../../api/models/template-info";
import { InDesignLink } from "../../api/models/indesign-link";

export interface CoObjectItem {
  documentId: string;
  contentControlId?: number;
  id?: string;
  info: CoObjectGeneralInfo;
  source?: CoObjectSource;
  parameters?: DimensionDetail[] | DimensionParameter[];
  advanced?: CoObjectAdvanced;
  isLocked?: boolean;
  originalPayload?: string;
}

export interface CellAddress {
  row: number;
  column: number;
}

export interface CoObjectCellItem extends CoObjectItem {
  cellAddress: CellAddress;
}

export interface CoObjectSummary {
  id: string;
  text?: string;
  documentId?: string;
  contentControlId?: number;
  objectName?: string;
  objectType?: CoObjectType;
  formatMetadata?: FormatMetadata;
  row?: number;
  column?: number;
}

export interface DBApplication {
  uid?: string;
  id: string;
  desc?: string;
}

export interface FormTable {
  code: string;
  desc: string;
  oid: string;
  oidPath: string[];
}

export interface SheetForm {
  uid?: string;
  code: string;
  desc: string;
}

export interface AreaChartSheet {
  name: string;
}

export interface CoObjectGeneralInfo {
  objectName: string;
  objectType: CoObjectType;
  info: string;
  overrideData?: boolean;
  overrideStyle?: boolean;
  isComponent?: boolean;
  componentReference?: ComponentReference;
}

export type CoObjectType = keyof typeof CoObjectTypeEnum;
export type CoObjectElabResultType = "HTML" | "STRINGBASE64" | "IMAGE_STRINGBASE64" | "REF_URL" | "TEXT";

export interface CoObjectSource {
  dimensions?: DimensionDetail[];
  type:
    | "TABLE"
    | "CHART"
    | "DROPDOWN_LIST"
    | "DOCUMENT_PART_INCLUDE"
    | "AMOUNT"
    | "AMOUNT_AIH"
    | "DIMENSION_ATTRIBUTE"
    | "TGK_TEXT";
  elabResultType?: CoObjectElabResultType;
  choice?: string;
}

export interface TgkTableObjectSource extends CoObjectSource {
  type: "TABLE" | "CHART";
  objectSource: string;
  dbApplication: DBApplication;
  form: FormTable;
  sheet: SheetForm;
  area: AreaChartSheet;
  chart: AreaChartSheet;
  path: string[];
  inheritFormatImage?: boolean;
  formatImage: FormatImage;
}

export interface TgkAmountObjectSource extends CoObjectSource {
  type: "AMOUNT" | "AMOUNT_AIH";
  form?: FormTable;
  sheet?: SheetForm;
  area?: AreaChartSheet;
  cell?: string;
  objectSource: string;
  dbApplication?: DBApplication;
  dimensions?: DimensionDetail[];
  analyticalWorkspace?: AnalyticalWorkspace;
  dataset?: Dataset;
  amount?: DatasetField;
  process?: Process;
}

export interface TgkDropDownSource extends CoObjectSource {
  type: "DROPDOWN_LIST";
  dropDownList: TgkDropDownItem[];
}

export interface TgkDimensionAttributeSource extends CoObjectSource {
  type: "DIMENSION_ATTRIBUTE";
  dbApplication: DBApplication;
  dimensions: DimensionDetail[];
}

export interface TgkTextSource {
  type: "TGK_TEXT";
  dbApplication: DBApplication;
  analyticalWorkspace: AnalyticalWorkspace;
  dataset: Dataset;
  richText: DatasetField;
  dimensions: DimensionDetail[];
  process: Process;
}

export interface CoObjectAdvanced {
  inheritFormat?: boolean;
  choice?: string;
  documentId?: string;
  logicId?: string;
  language?: string;
  formatMetadata?: FormatMetadata;
  formatImage?: FormatImage;
  templateInfo?: TemplateInfo;
  inDesignLink?: InDesignLink;
}

export const isObjectUpdatable = (coObjectItem: CoObjectItem): boolean => {
  let objectType = coObjectItem.info.objectType;
  return (
    objectType === "TABLE" ||
    objectType === "CHART" ||
    objectType === "AMOUNT" ||
    objectType === "AMOUNT_AIH" ||
    objectType === "DIMENSION_ATTRIBUTE" ||
    objectType === "TGK_TEXT" ||
    objectType === "DOCUMENT_PART_INCLUDE"
  );
};

export interface DocumentPartSource extends CoObjectSource {
  type: "DOCUMENT_PART_INCLUDE";
  path: string;
  includedDocumentPartId?: string;
}
